import { useCallback, useRef } from 'react';
import { Form, FormRenderProps } from 'react-final-form';

import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { Button } from 'UIPackage/MainModule/components/Button';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';

import styles from './UpdateKnmsStartDateModal.module.scss';

interface IUpdateKnmsStartDateForm {
  startDate?: string;
}

type IUpdateKnmsStartDateFormRenderProps = FormRenderProps<IUpdateKnmsStartDateForm>;

export interface IUpdateKnmsStartDateModalProps {
  visible?: boolean;
  selectedKnmsIds: string[];
  knms: IKnmRaw[];
  onApply: (startDate: string) => void;
  onClose: () => void;
}

export const UpdateKnmsStartDateModal = ({
  visible,
  knms,
  selectedKnmsIds,
  onApply,
  onClose,
}: IUpdateKnmsStartDateModalProps) => {
  const formRef = useRef<IUpdateKnmsStartDateFormRenderProps['form']>();

  const handleApplyButtonClick = useCallback(() => formRef.current && formRef.current.submit(), []);

  const handleFormSubmit = useCallback(
    (values: IUpdateKnmsStartDateForm) => {
      if (values.startDate) {
        onApply(values.startDate);
      }
    },
    [onApply],
  );

  const handleAfterClose = useCallback(() => formRef.current && formRef.current.reset(), []);

  const renderForm = useCallback(
    ({ form }: IUpdateKnmsStartDateFormRenderProps) => {
      formRef.current = form;

      const validateStarDate = (value: string) =>
        knms
          .filter(item => selectedKnmsIds.includes(item.id.toString()))
          .find(item => makeNoLaterThenValidator(true, () => item.stopDate)(value)) &&
        'Дата начала мероприятия не должна быть позже даты окончания мероприятия';

      return (
        <form className={styles.Form}>
          <div className="shared-field-row">
            <div className={styles.FieldLabelBlock}>
              <label className={styles.FieldLabel} htmlFor="updateKnmsStartDateInput">
                Выберите дату начала КНМ
              </label>
            </div>
            <DatePickerField
              className="shared-row-field"
              id="updateKnmsStartDateInput"
              name="startDate"
              validate={composeValidators(makeRequired('Поле не может быть пустым'), validateStarDate)}
            />
          </div>
        </form>
      );
    },
    [knms, selectedKnmsIds],
  );

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title={`Изменение даты начала КНМ (${selectedKnmsIds?.length})`} sizeText="small" />
      <ModalBody size="content" border="bottom">
        <Form<IUpdateKnmsStartDateForm> render={renderForm} onSubmit={handleFormSubmit} />
      </ModalBody>
      <ModalActions>
        <Button text="Изменить" color="primary" size="medium" type="button" onClick={handleApplyButtonClick} />
        <Button text="Отменить" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};
